const UI_PATH_PREFIXES = [
  '/billing',
  '/notices',
  '/ops',
  '/profile',
  '/requests',
  '/sales',
  '/signups',
  '/subscriptions',
  '/support',
  '/withdrawal-approvals',
  '/work',
]

function valueOf(payload, key) {
  const value = payload && payload[key]
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  return text || null
}

function hasUsableId(value) {
  if (value === null || value === undefined || value === '') return false
  return String(value) !== '0'
}

function encoded(value) {
  return encodeURIComponent(String(value))
}

function isLikelyUiPath(path) {
  if (!path || !path.startsWith('/')) return false
  if (path.startsWith('/v1/') || path.startsWith('/api/')) return false
  if (path === '/') return true
  return UI_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`))
}

export function normalizeNotificationHref(raw) {
  if (raw === null || raw === undefined) return null
  const href = String(raw).trim()
  if (!href) return null
  if (/^(https?:|mailto:|tel:)/i.test(href)) return href
  if (href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/\\') && !href.startsWith('/v1/') && !href.startsWith('/api/')) return href
  return null
}

export function isExternalNotificationHref(href) {
  return Boolean(href && /^(https?:|mailto:|tel:)/i.test(String(href).trim()))
}

export function resolveNotificationHref(payload) {
  const p = payload || {}
  const type = String(p.type || '').trim().toUpperCase()

  if (type === 'MATURITY_REMINDER' || (type === 'CALENDAR' && valueOf(p, 'title')?.startsWith('[만기고객안내]'))) {
    return '/support/maturity-ledger'
  }

  if (type === 'DAILY_WORK_BRIEFING') {
    const dailyBriefingLink = normalizeNotificationHref(valueOf(p, 'link'))
    return dailyBriefingLink && dailyBriefingLink !== '/' ? dailyBriefingLink : '/?focus=briefing'
  }

  if (type === 'SYSTEM_NOTICE_CREATED') {
    return hasUsableId(p.noticeId) ? `/ops/system-notices/${encoded(p.noticeId)}` : '/ops/system-notices'
  }
  if (type === 'BILLING_OPERATION_QUEUE') {
    return normalizeNotificationHref(valueOf(p, 'link')) || '/subscriptions/hub/operation-queue'
  }
  if (type === 'LOGIN_LOCKED' || type === 'EXTERNAL_API_RATE_LIMIT_BLOCKED') {
    return normalizeNotificationHref(valueOf(p, 'link')) || '/ops/history-management'
  }
  if (type === 'WITHDRAWAL_REQUEST_CREATED') {
    return '/withdrawal-approvals'
  }
  if (type === 'SIGNUP_APPROVED' || type === 'SIGNUP_REJECTED') {
    return '/profile'
  }
  if (type === 'SIGNUP_REQUEST_CREATED' || type === 'SIGNUP_AUTO_APPROVE_FAILED') {
    return hasUsableId(p.signupId) ? `/signups/${encoded(p.signupId)}` : '/signups'
  }
  if (String(p.action || '').trim().toUpperCase() === 'REMOTE_SIGNATURE_COMPLETED') {
    return '/work/tools?tool=after-contract-certificates'
  }

  for (const key of ['link', 'targetUrl', 'targetPath', 'landingUrl', 'url']) {
    const href = normalizeNotificationHref(valueOf(p, key))
    if (href) return href
  }

  const path = normalizeNotificationHref(valueOf(p, 'path'))
  if (path && isLikelyUiPath(path)) return path

  const claimId = p.claimId || p.claimApplicationId
  if (hasUsableId(claimId)) return `/support/claims/applications/${encoded(claimId)}`

  if (hasUsableId(p.requestId)) {
    const href = `/requests/${encoded(p.requestId)}`
    return hasUsableId(p.targetId) ? `${href}?targetId=${encoded(p.targetId)}` : href
  }
  if (type.startsWith('REQUEST_') || p.insurerOrgName || hasUsableId(p.targetId)) return '/requests'

  if (hasUsableId(p.inquiryId)) return `/support/inquiries/${encoded(p.inquiryId)}`
  if (hasUsableId(p.noticeId)) return `/notices/${encoded(p.noticeId)}`
  if (hasUsableId(p.leadId)) return `/sales/consultations?leadId=${encoded(p.leadId)}`
  if (type === 'CALENDAR' || hasUsableId(p.eventId) || hasUsableId(p.calendarEventId)) return '/work/calendar'

  return null
}

export function openNotificationHref(href, navigate, fallback = '/') {
  const resolved = normalizeNotificationHref(href)
  if (!resolved) {
    if (fallback && typeof navigate === 'function') {
      navigate(fallback)
      return true
    }
    return false
  }
  if (isExternalNotificationHref(resolved)) {
    window.location.assign(resolved)
    return true
  }
  if (typeof navigate !== 'function') return false
  navigate(resolved)
  return true
}
